import React from 'react';
import { CertQuestionMatrix } from '@unity-learning/shared';
import CodeMirror from '@uiw/react-codemirror';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { useTheme } from '../../contexts/ThemeContext';
import { ContextCode } from './ContextCode';

// Light theme for CodeMirror
const lightTheme = EditorView.theme({
    '&': { backgroundColor: '#f8f9fa' },
    '.cm-gutters': { backgroundColor: '#f0f0f0', borderRight: '1px solid #ddd' },
    '.cm-content': { caretColor: '#333' },
    '.cm-line': { color: '#333' },
});

interface QuestionMatrixProps {
    question: Omit<CertQuestionMatrix, 'validation'>;
    value: Record<string, number>;
    onChange: (value: Record<string, number>) => void;
    disabled?: boolean;
    /** Revue (page de résultats) : colonne attendue par ligne. Chaque ligne prend la couleur juste ou fausse. */
    reviewAgainst?: Record<string, number>;
}

export const QuestionMatrix: React.FC<QuestionMatrixProps> = ({
    question,
    value,
    onChange,
    disabled = false,
    reviewAgainst
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { content } = question;
    const { columns, rows, context_code } = content;

    const handleSelection = (rowId: string, columnIndex: number) => {
        if (disabled) return;
        onChange({
            ...value,
            [rowId]: columnIndex
        });
    };

    return (
        <div className="w-full">
            {/* Context Code (if present) */}
            {context_code && (
                <div className="mb-4">
                    <ContextCode code={context_code} />
                </div>
            )}

            {/* Header Row */}
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 mb-2 pb-2 border-b border-gray-300 dark:border-gray-600">
                <div className="font-medium text-gray-700 dark:text-gray-300">Énoncé</div>
                {columns.map((col, idx) => (
                    <div
                        key={idx}
                        className="w-20 text-center font-medium text-gray-700 dark:text-gray-300"
                    >
                        {col}
                    </div>
                ))}
            </div>

            {/* Body Rows */}
            <div className="space-y-2">
                {rows.map((row, rowIndex) => {
                    const reviewRight = reviewAgainst ? value[row.id] === reviewAgainst[row.id] : undefined;
                    return (
                    <div
                        key={row.id}
                        title={reviewRight === undefined ? undefined : (reviewRight ? 'Réponse juste' : 'Réponse fausse')}
                        className={`grid grid-cols-[1fr_auto_auto] gap-4 p-3 rounded-lg items-center ${reviewRight === undefined
                            ? (rowIndex % 2 === 0
                                ? 'bg-gray-50 dark:bg-gray-800/50'
                                : 'bg-white dark:bg-gray-900/30')
                            : (reviewRight
                                ? 'bg-green-50 dark:bg-green-950 border border-green-500 dark:border-green-400'
                                : 'bg-red-50 dark:bg-red-950 border border-red-500 dark:border-red-400')
                            }`}
                    >
                        {/* Row Content */}
                        <div className="text-gray-800 dark:text-gray-200">
                            {row.content_type === 'code' ? (
                                <div className="rounded-lg overflow-hidden">
                                    <CodeMirror
                                        value={row.value}
                                        theme={isDark ? oneDark : lightTheme}
                                        extensions={[cpp()]}
                                        height="auto"
                                        editable={false}
                                        basicSetup={{
                                            lineNumbers: false,
                                            foldGutter: false,
                                            highlightSelectionMatches: false,
                                        }}
                                    />
                                </div>
                            ) : (
                                <span className="text-sm leading-relaxed">{row.value}</span>
                            )}
                        </div>

                        {/* Radio Buttons */}
                        {columns.map((_, colIndex) => (
                            <div key={colIndex} className="w-20 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => handleSelection(row.id, colIndex)}
                                    disabled={disabled}
                                    className={`
                    w-8 h-8 rounded-full border-2 flex items-center justify-center
                    transition-all duration-200
                    ${value[row.id] === colIndex
                                            ? 'border-blue-500 bg-blue-500 text-white'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                                        }
                    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                  `}
                                    aria-label={`Sélectionner « ${columns[colIndex]} » pour la ligne ${rowIndex + 1}`}
                                >
                                    {value[row.id] === colIndex && (
                                        <div className="w-2 h-2 bg-white rounded-full" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default QuestionMatrix;
